<template>
  <NodePage v-if="pageNode" v-bind="pageNode">
    <div class="container mt-30 lg:mt-50">
      <div class="tags flex flex-row flex-wrap mb-60">
        <FilterTag
          :label="$texts('allBlogposts', 'All blogposts')"
          query-key="tag"
        />
        <FilterTag
          v-for="t in tags"
          :key="t.id"
          query-key="tag"
          :query-value="t.id"
          :label="t.name"
        />
      </div>

      <Transition name="fade" mode="out-in">
        <div :key="tag || 'all'">
          <div
            v-for="item in itemsVisible"
            :key="item.node.uuid"
            class="flex flex-col mb-60"
          >
            <NodeBlogArticleTeaser v-bind="item.node" :key="item.node.uuid" />
          </div>
        </div>
      </Transition>
      <LoadMore
        :per-page="perPage"
        :page="pageNumber"
        :total="itemsFiltered.length"
      />
    </div>
  </NodePage>
</template>

<script lang="ts" setup>
import type {
  NodeBlogArticleTeaserFragment,
  NodePageFragment,
} from '#build/graphql-operations'
import { falsy, filterByTypenames } from '~/helpers/type'

definePageMeta({
  name: 'newsroom',
  path: '/newsroom',
  drupalFrontendRoute: true,
  drupalRoute: true,
  languageMapping: {
    en: '/newsroom',
    fr: '/newsroom',
  },
})

const route = useRoute()
const tag = useQueryString('tag')
const page = useQueryString('page')
const pageNumber = computed(() => {
  if (page.value) {
    const v = parseInt(page.value)
    if (Number.isInteger(v)) {
      return v
    }
  }

  return 0
})
const perPage = 4

const { data: query } = await useAsyncGraphqlQuery(
  'routeNewsroom',
  {
    path: route.path,
  },
  {
    graphqlCaching: {
      client: true,
    },
    transform: function (data) {
      return data.data
    },
  },
)

const { entity: pageNode, metatags } = await useDrupalRoute<NodePageFragment>(
  query.value,
)
defineSchemaOrg(metatags)

type NewsroomItem = {
  tags: string[]
  node: NodeBlogArticleTeaserFragment
}

const items = computed<NewsroomItem[]>(() =>
  filterByTypenames(query.value?.entityQuery.items, 'NodeBlogArticle').map(
    (node) => {
      const tags = node.tags?.map((v) => v.id).filter(falsy) || []
      return {
        tags,
        node,
      }
    },
  ),
)

const itemsFiltered = computed(() =>
  items.value.filter((item) => {
    if (tag.value) {
      return item.tags.includes(tag.value)
    }
    return true
  }),
)

const itemsVisible = computed(() =>
  itemsFiltered.value.slice(0, (pageNumber.value + 1) * perPage),
)

type Tag = {
  id: string
  name: string
}

const tags = computed(() =>
  Object.values(
    items.value
      .flatMap((v) => v.node.tags)
      .reduce<Record<string, Tag>>((acc, tag) => {
        if (tag?.id && tag.name && !acc[tag.id]) {
          acc[tag.id] = {
            id: tag.id,
            name: tag.name,
          }
        }
        return acc
      }, {}),
  ),
)

await renderPageDependencies()
</script>
